import { uuidv7 } from 'uuidv7';
import { z } from 'zod';
import {
  LandingSurfaceTypeSchema,
  type CreateLandingTouchRequest,
  type CreateLandingTouchResponse,
} from '@/contracts';
import { UuidSchema } from '@/contracts/uuid';

const ANON_ID_STORAGE_KEY = 'dwellbot_anon_id';
const ACTIVE_ATTRIBUTION_STORAGE_KEY = 'dwellbot_active_attribution';
const ATTRIBUTION_TOKEN_QUERY_KEY = 'attribution_token';

const ActiveAttributionStateSchema = z.object({
  attributionToken: z.string().min(1),
  anonId: UuidSchema,
  landingTouchId: UuidSchema.nullable(),
  landingSurfaceType: LandingSurfaceTypeSchema,
  landingSurfaceKey: z.string().nullable(),
  challengePublicId: z.string().nullable(),
  challengeShareId: UuidSchema.nullable(),
});

type MarketingFields = {
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmId: string | null;
  utmContent: string | null;
  utmTerm: string | null;
  fbclid: string | null;
  ttclid: string | null;
  twclid: string | null;
  liFatId: string | null;
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  msclkid: string | null;
  aleid: string | null;
};

export type ActiveAttributionState = z.infer<typeof ActiveAttributionStateSchema>;

const normalizeOptionalValue = (value: string | null): string | null => {
  if (value === null) {
    return null;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return null;
  }

  return trimmedValue;
};

const readCurrentUrl = (): URL => {
  const currentUrl = new URL(window.location.href);

  return currentUrl;
};

const buildRelativeUrl = (url: URL): string => {
  const relativeUrl = `${url.pathname}${url.search}${url.hash}`;

  return relativeUrl;
};

const removeAttributionToken = (url: URL): URL => {
  const sanitizedUrl = new URL(url.toString());
  sanitizedUrl.searchParams.delete(ATTRIBUTION_TOKEN_QUERY_KEY);

  return sanitizedUrl;
};

const readMarketingFields = (url: URL): MarketingFields => {
  const searchParams = url.searchParams;
  const marketingFields = {
    utmSource: normalizeOptionalValue(searchParams.get('utm_source')),
    utmMedium: normalizeOptionalValue(searchParams.get('utm_medium')),
    utmCampaign: normalizeOptionalValue(searchParams.get('utm_campaign')),
    utmId: normalizeOptionalValue(searchParams.get('utm_id')),
    utmContent: normalizeOptionalValue(searchParams.get('utm_content')),
    utmTerm: normalizeOptionalValue(searchParams.get('utm_term')),
    fbclid: normalizeOptionalValue(searchParams.get('fbclid')),
    ttclid: normalizeOptionalValue(searchParams.get('ttclid')),
    twclid: normalizeOptionalValue(searchParams.get('twclid')),
    liFatId: normalizeOptionalValue(searchParams.get('li_fat_id')),
    gclid: normalizeOptionalValue(searchParams.get('gclid')),
    gbraid: normalizeOptionalValue(searchParams.get('gbraid')),
    wbraid: normalizeOptionalValue(searchParams.get('wbraid')),
    msclkid: normalizeOptionalValue(searchParams.get('msclkid')),
    aleid: normalizeOptionalValue(searchParams.get('aleid')),
  };

  return marketingFields;
};

const readStoredValue = (storageKey: string, storage: Storage): string | null => {
  const storedValue = storage.getItem(storageKey);

  return storedValue;
};

const writeStoredValue = (storageKey: string, storage: Storage, value: string): void => {
  storage.setItem(storageKey, value);
};

export const getOrCreateAnonId = (): string => {
  const storedAnonId = readStoredValue(ANON_ID_STORAGE_KEY, window.localStorage);
  const parsedAnonId = UuidSchema.safeParse(storedAnonId);

  if (parsedAnonId.success) {
    return parsedAnonId.data;
  }

  const createdAnonId = uuidv7();
  writeStoredValue(ANON_ID_STORAGE_KEY, window.localStorage, createdAnonId);

  return createdAnonId;
};

export const buildChallengeAttributionState = (input: {
  attributionToken: string;
  anonId: string;
  landingTouchId: string | null;
  challengePublicId: string;
  challengeShareId: string | null;
}): ActiveAttributionState => {
  const state = ActiveAttributionStateSchema.parse({
    attributionToken: input.attributionToken,
    anonId: input.anonId,
    landingTouchId: input.landingTouchId,
    landingSurfaceType: 'challenge',
    landingSurfaceKey: input.challengePublicId,
    challengePublicId: input.challengePublicId,
    challengeShareId: input.challengeShareId,
  });

  return state;
};

export const buildChallengeAttributionStateFromLandingTouch = (
  challengePublicId: string,
  response: CreateLandingTouchResponse
): ActiveAttributionState => {
  const state = buildChallengeAttributionState({
    attributionToken: response.attribution_token,
    anonId: response.anon_id,
    landingTouchId: response.landing_touch_id,
    challengePublicId,
    challengeShareId: response.challenge_share_id ?? null,
  });

  return state;
};

export const readStoredAttributionState = (): ActiveAttributionState | null => {
  const storedState = readStoredValue(ACTIVE_ATTRIBUTION_STORAGE_KEY, window.sessionStorage);

  if (storedState === null) {
    return null;
  }

  try {
    const parsedState = JSON.parse(storedState) as unknown;
    const validatedState = ActiveAttributionStateSchema.parse(parsedState);

    return validatedState;
  } catch {
    window.sessionStorage.removeItem(ACTIVE_ATTRIBUTION_STORAGE_KEY);

    return null;
  }
};

export const storeAttributionState = (state: ActiveAttributionState): void => {
  const serializedState = JSON.stringify(state);
  writeStoredValue(ACTIVE_ATTRIBUTION_STORAGE_KEY, window.sessionStorage, serializedState);
};

export const clearStoredAttributionState = (): void => {
  window.sessionStorage.removeItem(ACTIVE_ATTRIBUTION_STORAGE_KEY);
};

export const readAttributionTokenFromUrl = (): string | null => {
  const currentUrl = readCurrentUrl();
  const tokenValue = currentUrl.searchParams.get(ATTRIBUTION_TOKEN_QUERY_KEY);
  const token = normalizeOptionalValue(tokenValue);

  return token;
};

export const clearAttributionTokenFromUrl = (): void => {
  const currentUrl = readCurrentUrl();
  const sanitizedUrl = removeAttributionToken(currentUrl);
  const relativeUrl = buildRelativeUrl(sanitizedUrl);
  window.history.replaceState(window.history.state, '', relativeUrl);
};

export const buildChallengeAuthReturnUrl = (attributionToken: string): string => {
  const currentUrl = readCurrentUrl();
  const sanitizedUrl = removeAttributionToken(currentUrl);
  sanitizedUrl.searchParams.set(ATTRIBUTION_TOKEN_QUERY_KEY, attributionToken);
  const returnUrl = sanitizedUrl.toString();

  return returnUrl;
};

export const buildChallengeLandingTouchRequest = (
  challengePublicId: string
): CreateLandingTouchRequest => {
  const anonId = getOrCreateAnonId();
  const currentUrl = readCurrentUrl();
  const sanitizedUrl = removeAttributionToken(currentUrl);
  const marketingFields = readMarketingFields(sanitizedUrl);
  const request = {
    landing_surface_type: 'challenge',
    landing_surface_key: challengePublicId,
    challenge_public_id: challengePublicId,
    anon_id: anonId,
    landing_url: sanitizedUrl.toString(),
    referrer_url: normalizeOptionalValue(document.referrer),
    utm_source: marketingFields.utmSource,
    utm_medium: marketingFields.utmMedium,
    utm_campaign: marketingFields.utmCampaign,
    utm_id: marketingFields.utmId,
    utm_content: marketingFields.utmContent,
    utm_term: marketingFields.utmTerm,
    fbclid: marketingFields.fbclid,
    ttclid: marketingFields.ttclid,
    twclid: marketingFields.twclid,
    li_fat_id: marketingFields.liFatId,
    gclid: marketingFields.gclid,
    gbraid: marketingFields.gbraid,
    wbraid: marketingFields.wbraid,
    msclkid: marketingFields.msclkid,
    aleid: marketingFields.aleid,
  } satisfies CreateLandingTouchRequest;

  return request;
};

export const readChallengeMarketingFields = (): MarketingFields => {
  const currentUrl = readCurrentUrl();
  const sanitizedUrl = removeAttributionToken(currentUrl);
  const marketingFields = readMarketingFields(sanitizedUrl);

  return marketingFields;
};
